// Who carries a client book of their own.
//
// Dena and Veronica are org admins who don't advise — they build scenarios for
// Andrew and Dylan. Their My Clients page is always empty, and the New Client
// button sitting on it files the client under them instead of the advisor it
// was for. `profiles.has_client_book` (migration 20260917000002) marks those
// people, and this is the single rule both the sidebar and the dashboard's
// landing view read, so the two can never drift apart.
//
// The role model cannot express this on its own: `user_roles_unique_with_org`
// is unique on (user_id, org_id), so one person holds exactly one role per org
// and Dylan cannot be org_admin AND agent at the same time.

export interface ClientBookContext {
  /** profiles.has_client_book. `undefined` before the migration is applied. */
  hasClientBook?: boolean | null;
  isSuperAdmin: boolean;
  isOrgOwner: boolean;
  isOrgAdmin: boolean;
}

/**
 * True when this person's personal client surfaces (My Clients, Drafts) should
 * be hidden and All Clients becomes their home.
 *
 * Fails open in every uncertain case — a missing column, a null, or a plain
 * agent all keep their book, so a half-applied migration can never strand
 * somebody with no clients screen.
 */
export function hidesPersonalClients(ctx: ClientBookContext): boolean {
  // Only an explicit false hides anything. Pre-migration rows read back as
  // undefined and must behave exactly like today.
  if (ctx.hasClientBook !== false) return false;
  // Super admins have their own shell and never render the My Work lane.
  if (ctx.isSuperAdmin) return false;
  // Agents always keep My Clients: it is their only surface, and All Clients
  // is not in their sidebar, so hiding it would leave them nowhere to work.
  return ctx.isOrgOwner || ctx.isOrgAdmin;
}

/**
 * True when this person can own a client — the All Clients "New Client"
 * advisor picker lists only these. A support admin (has_client_book = false)
 * builds work for the advisors and never owns it, so they are not offered as
 * the owner. Fails open like the rule above: only an explicit false excludes.
 */
export function carriesClientBook(hasClientBook?: boolean | null): boolean {
  return hasClientBook !== false;
}
